import type { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { ClientError } from "graphql-request";
import type { FetchError } from "../types";

/* eslint-disable @typescript-eslint/no-explicit-any */
export const isQuery = (query: TypedDocumentNode<any, any>) =>
  query.definitions?.some((def: any) => def.operation === "query");

export const isGqlError = (err: unknown): err is ClientError =>
  err instanceof ClientError;

export const isAbortError = (err: unknown): err is DOMException =>
  err instanceof DOMException && err.name === "AbortError";

export const isNetworkError = (err: unknown): err is TypeError =>
  err instanceof TypeError;

export const is401 = (err: unknown): boolean =>
  isGqlError(err) && err.response.status === 401;

export const is403 = (err: unknown): boolean =>
  isGqlError(err) && err.response.status === 403;

export const is5xx = (err: unknown): boolean =>
  isGqlError(err) && err.response.status >= 500 && err.response.status < 600;

export const isTimeout = (err: unknown): boolean =>
  err instanceof Error && err.message === "Request timed out";

export const hasGqlErrors = (err: unknown): err is ClientError =>
  isGqlError(err) && Array.isArray(err.response.errors);

export const hasPartialData = <T>(
  err: unknown,
): err is ClientError & { response: { data: T } } =>
  isGqlError(err) && err.response.data != null;

export const mapError = (err: unknown): FetchError => {
  if (isNetworkError(err)) {
    return { type: "network", error: err };
  }
  if (isTimeout(err)) {
    return { type: "timeout", error: err as Error };
  }
  if (isAbortError(err)) {
    return { type: "abort", error: err as Error };
  }
  if (is401(err) || is403(err)) {
    return { type: "not_authenticated", error: err as ClientError };
  }
  if (isGqlError(err)) {
    return { type: "graphql", error: err, status: err.response.status };
  }
  return {
    type: "network",
    error: new TypeError(err instanceof Error ? err.message : "Unknown error"),
  };
};
